import React, { useCallback, useEffect, useRef, useState } from 'react';
import UiIcon from '../ui/UiIcon';
import BitcoinSendPanel from './BitcoinSendPanel';

const LedgerApp = ({ wallet, active }) => {
  const timersRef = useRef([]);
  const [pin, setPin] = useState('');
  const [phase, setPhase] = useState('locked');
  const [sendOpen, setSendOpen] = useState(false);
  const sendButtonRef = useRef(null);
  const bitcoinAddress = wallet.publicAddresses.find(item => item.symbol === 'BTC')?.address;
  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
  }, []);
  useEffect(() => clearTimers, [clearTimers]);
  const delay = (callback, duration) => timersRef.current.push(window.setTimeout(callback, duration));

  const enterDigit = (digit) => {
    if (!['locked', 'invalid'].includes(phase)) return;
    if (phase === 'invalid') {
      clearTimers(); setPhase('locked'); setPin(digit); return;
    }
    if (pin.length >= 4) return;
    const candidate = `${pin}${digit}`;
    setPin(candidate);
    if (candidate.length !== 4) return;
    setPhase('verifying');
    delay(() => {
      if (candidate === wallet.demoPin) {
        setPhase('granted');
        delay(() => setPhase('unlocked'), 480);
      } else {
        setPhase('invalid');
        delay(() => { setPin(''); setPhase('locked'); }, 720);
      }
    }, 520);
  };
  const removeDigit = () => { if (phase === 'locked') setPin((current) => current.slice(0, -1)); };

  useEffect(() => {
    if (!active || phase === 'unlocked') return undefined;
    const handleKeyDown = (event) => {
      if (event.target.matches?.('input, textarea, select')) return;
      if (/^[0-9]$/.test(event.key)) enterDigit(event.key);
      if (event.key === 'Backspace') { event.preventDefault(); removeDigit(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const statusCopy = phase === 'verifying' ? 'VERIFYING…' : phase === 'granted' ? 'ACCESS GRANTED' : phase === 'invalid' ? 'INVALID PIN' : 'ENTER PIN';
  return <div className={`ledger-app is-${phase}`}>
    <div className="app-location"><span>ledger.exe / public addresses</span><span>PUBLIC DIRECTORY</span></div>
    {phase !== 'unlocked' ? <div className="wallet-lock-panel">
      <header aria-live="polite"><span>PUBLIC VIEWER / DEMO</span><strong>{statusCopy}</strong><p>This interaction protects no real wallet. Demo access code <b>1111</b>.</p><div className="wallet-pin-slots" aria-label={`${pin.length} of 4 PIN digits entered`}>{[0, 1, 2, 3].map((slot) => <i className={slot < pin.length ? 'is-filled' : ''} key={slot} />)}</div></header>
      <div className="wallet-keypad" aria-label="Demo PIN keypad">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => <button type="button" onClick={() => enterDigit(String(digit))} disabled={!['locked', 'invalid'].includes(phase)} aria-label={`Enter ${digit}`} key={digit}>{digit}</button>)}
        <button type="button" className="wallet-keypad__clear" onClick={() => setPin('')} disabled={phase !== 'locked'}>CLR</button>
        <button type="button" onClick={() => enterDigit('0')} disabled={!['locked', 'invalid'].includes(phase)} aria-label="Enter 0">0</button>
        <button type="button" onClick={removeDigit} disabled={phase !== 'locked'} aria-label="Delete last digit"><UiIcon name="backspace" /></button>
      </div>
      <small>Keyboard numbers supported. Public address viewer only; no signing capability.</small>
    </div> : <div className="wallet-portfolio">
      <header><div><span>PUBLIC ADDRESS DIRECTORY</span><h2>Public portfolio</h2></div><strong>{wallet.publicAddresses.length} PUBLIC ADDRESSES</strong></header>
      <section className="wallet-assets" aria-labelledby="wallet-assets-title"><div className="wallet-panel-heading"><h3 id="wallet-assets-title">Addresses</h3><span>{wallet.assets.length} configured networks</span></div>{wallet.assets.map((asset) => {
        const address = wallet.publicAddresses.find((item) => item.symbol === asset.symbol);
        return <article key={asset.symbol}><i>{asset.symbol.slice(0, 1)}</i><div><strong>{asset.symbol}</strong><small>{asset.name} / {asset.network}</small></div>{asset.symbol === 'BTC' && address && <button ref={sendButtonRef} className="wallet-send-button" type="button" aria-label="Send Bitcoin" aria-expanded={sendOpen} aria-controls="bitcoin-send-panel" onClick={() => setSendOpen(current => !current)}>Send <UiIcon name="external" /></button>}{address ? <code>{address.address}</code> : <span>Not configured</span>}</article>;
      })}</section>
      {sendOpen && bitcoinAddress && <BitcoinSendPanel address={bitcoinAddress} onClose={() => { setSendOpen(false); sendButtonRef.current?.focus(); }} />}
      <p className="wallet-security-note">Viewer only. Never enter a seed phrase, private key, exchange secret, or signing credential into this portfolio. Balances and transactions are not fetched.</p>
    </div>}
  </div>;
};

export default LedgerApp;
